import React, { useEffect, useState } from "react";
import l10n from "../../services/localization";

function Address({
  value = null, // Can be a Place or a string
  label,
  optional,
  tooltip = "",
  updateValue,
  validator = () => false,
  forceValidation = false,
  readOnly = false,
  scrollIntoView = false,
  placeholder = "",
  hint = "",
}) {
  const [isValidating, setIsValidating] = useState(forceValidation);
  const [place, setPlace] = useState(value);

  const ref = React.useRef(null);
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (scrollIntoView) {
      ref.current.focus();
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [ref]);

  useEffect(() => {
    // Set up the address autocompletion widget.
    const completer = new window.google.maps.places.Autocomplete(inputRef.current);
    completer.setTypes(["address"]);
    completer.addListener("place_changed", () => {
      // Hide the autocomplete dropdown and trigger the blur handler.
      inputRef.current.dispatchEvent(new Event("blur"), { bubbles: true });
    });

    inputRef.current.addEventListener("blur", () => {
      let place = completer.getPlace();
      inputRef.current.value = place ? place.formatted_address : "";
      setPlace(place);
      if (!validator(place)) updateValue(place);
    });

    // We don't want users to think they can edit the formatted address,
    // so just make them start over when they click into the address field.
    inputRef.current.addEventListener("focus", () => {
      inputRef.current.value = "";
    });
  }, [inputRef]);

  useEffect(() => {
    // if there is an input value when component mounts, show validation
    value && setIsValidating(true);
  }, []);

  useEffect(() => {
    forceValidation && setIsValidating(true);
  }, [forceValidation]);

  return (
    <label className={"label-container " + (isValidating && !!validator(place) ? "invalid" : "")}>
      <div aria-hidden className="label-flex">
        <div ref={ref}>
          {label} {optional ? `(${l10n.getString("labels.optional")})` : "*"}
        </div>
        {!!tooltip && (
          <div className="tooltip-button">
            <strong>?</strong>
            <div className="tooltip">{tooltip}</div>
          </div>
        )}
      </div>
      <div style={{ marginBottom: "25px" }}>
        <input
          defaultValue={value ? value.formatted_address || value : ""}
          ref={inputRef}
          aria-invalid={!!validator(place)}
          aria-label={validator("") + ", " + tooltip}
          onBlur={() => setIsValidating(true)}
          readOnly={readOnly}
          title={label}
          disabled={readOnly}
          placeholder={placeholder}
        />
      </div>
      <div aria-hidden className="validation">
        {validator(place)}&nbsp;
      </div>
      {!!hint && <div className="hint">{hint}</div>}
    </label>
  );
}

export default Address;
